/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ReactChild } from 'react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Button, Dialog, InlineNotification, Link, PasswordField, TextField, Typography2 } from '@coursera/cds-core';
import type { EOIButtonSource } from '@coursera/event-pulse-types';

import InternationalPhoneNumberInput from 'bundles/account-profile/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { ProfilePhone } from 'bundles/account-profile/components/types';
import Separator from 'bundles/authentication-common/components/Separator';
import * as actions from 'bundles/authentication-common/components/actions';
import {
  ARKOSE_LOGIN_KEY,
  LOGIN_REDESIGN_RECAPTCHA_ID,
  RECAPTCHA_ACTIONS,
} from 'bundles/authentication-common/constants/botManager';
import {
  EMAIL_CONSTRAINT as isEmailValid,
  PASSWORD_CONSTRAINT as isPasswordValid,
} from 'bundles/authentication-common/constants/constraints';
import useBotManager from 'bundles/authentication-common/hooks/useBotManager';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication/utils/loginUtils';
import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import EoiSSO from 'bundles/expression-of-interest/components/authentication/EoiSSO';
import { ACTION_QUERY_PARAMS, CONTINUE_EOI_PARAMS, SECTION_EVENT_MAP } from 'bundles/expression-of-interest/constants';
import type { ActionQueryParam } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import {
  getDefaultError,
  isDestinationDegree,
  setActionQueryParamAndReload,
} from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';
import Instrumentation from 'bundles/userModal/lib/instrumentation';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  learnerEmail: string;
  phone: Phone;
  handleEmailChange: (email: string) => void;
  handlePhoneChange: (phone: ProfilePhone) => void;
  handlePreviousSection: () => void;
  handleSubmit: () => Promise<void | {}>;
  trackingProductId: string;
  shouldSetAutoFocus?: boolean;
  navigateConfirmation?: () => void;
  eoiButtonSource?: EOIButtonSource;
};

const styles = {
  primaryButton: css`
    margin-right: var(--cds-spacing-200);
  `,
  reCAPTCHA: css`
    display: none;
  `,
  passwordField: css`
    margin-top: var(--cds-spacing-150);
    margin-bottom: var(--cds-spacing-150);
  `,
  supportText: css`
    margin-top: var(--cds-spacing-150);
  `,
};

export const LoginSection = (props: Props): React.ReactElement => {
  const {
    learnerEmail,
    phone,
    handleEmailChange,
    handlePreviousSection,
    handlePhoneChange,
    handleSubmit,
    trackingProductId,
    shouldSetAutoFocus,
    navigateConfirmation,
    eoiButtonSource,
  } = props;
  const { trackInteractEoi, trackSubmitEoi } = useEoiEventing(trackingProductId);
  const { slug } = useEoiOptimizationsContext();
  const location = useLocation();
  const navigate = useNavigate();

  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const verificationCodeInput = useRef<HTMLInputElement | null>(null);
  const [password, setPassword] = useState<string>('');
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [hasPhoneError, setHasPhoneError] = useState<boolean>(false);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [has2FA, setHas2FA] = useState<boolean>(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [formError, setFormError] = useState<string | ReactChild>('');

  const {
    isRetrievingTokenRef,
    getTokenData: getBotManagerTokenData,
    reset: resetBotManager,
  } = useBotManager({
    arkoseBotManager: {
      publicKey: ARKOSE_LOGIN_KEY,
    },
    reCAPTCHA: {
      container: LOGIN_REDESIGN_RECAPTCHA_ID,
      options: { action: RECAPTCHA_ACTIONS.login },
    },
  });

  const isDestinationDegreeEoi = isDestinationDegree(slug);

  const disclaimerText = isDestinationDegreeEoi
    ? _t(
        'By sharing my phone number, I consent to receiving calls, texts or emails from Coursera and the university about educational opportunities.'
      )
    : _t(
        'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities.'
      );

  const baseTrackingData = {
    eoiSection: SECTION_EVENT_MAP.loginSection,
    eoiButtonSource,
  };

  useEffect(() => {
    const hasPasswordError = isPasswordValid(password);

    if (hasPasswordError) {
      setPasswordError(hasPasswordError);
    } else {
      setPasswordError('');
    }
  }, [password]);

  useEffect(() => {
    const hasEmailError = isEmailValid(learnerEmail);
    if (hasEmailError) {
      setEmailError(hasEmailError);
    } else {
      setEmailError('');
    }
  }, [learnerEmail]);

  useEffect(() => {
    if (phone.phoneNumber === '') {
      setHasPhoneError(true);
    } else {
      setHasPhoneError(false);
    }
  }, [phone]);

  const handleError = (response: $TSFixMe) => {
    trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'api_failure' });

    resetBotManager();

    const { errorCode: code } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(code)) {
      const errorMessages = errorMessageFactory();
      const errorCode = code as keyof typeof errorMessages;

      if (errorCode === 'invalidCredential') {
        setFormError(errorMessages[errorCode]());
        verificationCodeInput.current?.focus();
      } else {
        setFormError(errorMessages[errorCode]);
      }

      setHas2FA(NEEDS_2FA_ERRORS.includes(code));
      setIsRunning(false);
    } else {
      setFormError(getDefaultError());
    }
  };

  const handleLogin = async () => {
    // Exit early if a token retrieval is in progress to prevent multiple function calls.
    if (isRetrievingTokenRef?.current) return false;

    if (passwordError || emailError || hasPhoneError) {
      return setHasFormError(true);
    }

    let tokenData;
    try {
      tokenData = await getBotManagerTokenData();
    } catch {
      return setFormError(getDefaultError());
    }

    setIsRunning(true);

    const eventData = {
      namespace: {
        app: 'degree-description-app',
        page: 'login',
      },
    };
    trackAuthAttempts(eventData, 'login');

    const payload = {
      email: learnerEmail,
      password,
      ...tokenData,
      ...(verificationCode && { mfa: verificationCode }),
    };

    try {
      trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'login' });

      await actions.login(payload);
      Instrumentation.login();

      trackSubmitEoi({ ...baseTrackingData, eoiStatus: 'success' });
      handleSubmit();

      if (navigateConfirmation) {
        // Add to implment the application url redirect after login success for Apply now button
        // The onLogin call needs to trigger the page refresh after the application url redirect
        navigateConfirmation();
        actions.onLogin();
      } else {
        // The onLogin call needs to trigger the page refresh before the query param update
        actions.onLogin();

        const actionFromQuery = simplifyQueryValue(location?.query?.action) ?? ''; // TODO handle null/undefined case
        const action: ActionQueryParam = Object.values(ACTION_QUERY_PARAMS).includes(actionFromQuery)
          ? actionFromQuery
          : ACTION_QUERY_PARAMS.EMAIL_ME_INFO;
        return setActionQueryParamAndReload(action, location, navigate, CONTINUE_EOI_PARAMS.CONTINUE_FROM_LOGIN);
      }
    } catch (e) {
      return handleError(e);
    }
  };

  const handleBack = () => {
    trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'back' });
    handlePreviousSection();
  };

  const handleForgotPassword = () => {
    trackInteractEoi({ ...baseTrackingData, eoiInteraction: 'forgot_password' });
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <>
      <HeadingGroup
        supportText={_t(
          'After logging in, you will receive information on upcoming enrollment dates, webinars, and program news via email.'
        )}
      >
        {_t('Welcome back!')}
      </HeadingGroup>
      <Content>
        <>
          <div css={styles.reCAPTCHA} id={LOGIN_REDESIGN_RECAPTCHA_ID} data-testid="recaptcha-signup" />
          <EoiSSO trackingProductId={trackingProductId} shouldSetAutoFocus={shouldSetAutoFocus} />
          <Separator text={_t('or')} />
        </>

        {formError && <InlineNotification severity="error">{formError}</InlineNotification>}

        <TextField
          label={_t('Email')}
          type="email"
          placeholder={_t('name@email.com')}
          fullWidth
          onChange={(e) => handleEmailChange(e.target.value)}
          ref={verificationCodeInput}
          validationStatus={hasFormError && !!emailError ? 'error' : undefined}
          validationLabel={emailError}
          value={learnerEmail}
          autoComplete="email"
        />

        <div css={styles.passwordField}>
          <PasswordField
            label={_t('Password')}
            placeholder={_t('Enter your password')}
            fullWidth
            onChange={(e) => setPassword(e.target.value)}
            validationStatus={hasFormError && !!passwordError ? 'error' : undefined}
            validationLabel={passwordError}
            value={password}
          />
          <Link
            href="/reset"
            target="_blank"
            variant="quiet"
            typographyVariant="bodySecondary"
            onClick={handleForgotPassword}
          >
            {_t('Forgot password?')}
          </Link>
        </div>

        <InternationalPhoneNumberInput
          profile={{ phone }}
          isOptional={false}
          isEmptyValid={false}
          customStyles={customFormattedPhoneStyles}
          submissionError={hasFormError}
          handleFormChange={handlePhoneChange}
          handleError={(err) => {
            if (err) {
              setHasFormError(true);
            }
          }}
          ref={phoneNumberRef}
        />

        <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.supportText}>
          {disclaimerText}
        </Typography2>

        {has2FA && (
          <TextField
            label={_t('Verification code')}
            type="number"
            placeholder="000000"
            fullWidth
            onChange={(e) => setVerificationCode(e.target.value)}
            ref={verificationCodeInput}
            value={verificationCode}
            autoComplete="one-time-code"
          />
        )}
      </Content>
      <Actions>
        <Button onClick={handleLogin} css={styles.primaryButton} loading={isRunning} disabled={isRunning}>
          {_t('Login and submit')}
        </Button>

        <Button onClick={handleBack} variant="secondary">
          {_t('Back')}
        </Button>
      </Actions>
    </>
  );
};

export default LoginSection;
